import { cloudRunViewerTf } from "@p0security/integrations-gcloud-shared/cloud-run-iac";
import type { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import type { GcpServiceAccountComponents } from "@p0security/integrations-gcp-service-account-shared/components";
import { CONNECTOR_CUSTOM_ROLE } from "@p0security/integrations-gcp-service-account-shared/constants";
import {
  type FrontendInstaller,
  integrationOptionProvider,
} from "@p0security/integrations-installer";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import type { Instructions } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

export type GcpServiceAccountInstaller = FrontendInstaller<
  typeof GcpServiceAccountComponents
>;

const { Paragraph, Text } = Typography;

/** Pinned by tag and digest, like the other connector images. */
const CONNECTOR_IMAGE =
  "docker.io/p0security/p0-connector-gcp-service-account:sha-841d828@sha256:f88ed5ce5e7aeab441473bf0cd4b8edf7cd3aba7f3d7c61064bca075796f8c57";

/** APIs the connector's resources, and the connector itself, call.
 *
 * Listed in the Terraform as a prerequisite rather than enabled by it: other
 * resources in the customer's project depend on these APIs too, so their
 * lifecycle is not this connector's to own.
 */
const REQUIRED_SERVICES = [
  "cloudresourcemanager.googleapis.com",
  "iam.googleapis.com",
  "run.googleapis.com",
];

/** The connector, its dedicated identity, and the one role that identity holds.
 *
 * PLACEHOLDER. Written out resource by resource because the
 * `p0-security/p0-connector/google` module only deploys VPC-bound `cloudsql`
 * connectors today. Replace with a module call once it can deploy this service
 * without VPC egress.
 */
export const gcpServiceAccountTerraform = (params: {
  projectId: string;
  region: string;
  connectorServiceName: string;
  connectorServiceAccountId: string;
  invokerServiceAccount: string;
}): string => {
  const {
    projectId,
    region,
    connectorServiceName,
    connectorServiceAccountId,
    invokerServiceAccount,
  } = params;
  const permissions = CONNECTOR_CUSTOM_ROLE.permissions
    .map((permission) => `    "${permission}",`)
    .join("\n");
  const services = REQUIRED_SERVICES.map((service) => `#   - ${service}`).join(
    "\n"
  );
  return `
# PLACEHOLDER: replace with the p0-security/p0-connector/google module once it
# supports the gcp-service-account service.
#
# Requires these APIs to be enabled in the project:
${services}

resource "google_project_iam_custom_role" "p0_agentic_identity_manager" {
  project     = "${projectId}"
  role_id     = "${CONNECTOR_CUSTOM_ROLE.id}"
  title       = "${CONNECTOR_CUSTOM_ROLE.title}"
  description = "Lets P0's connector create and delete agentic session service accounts."
  permissions = [
${permissions}
  ]
}

resource "google_service_account" "p0_service_account_connector" {
  project      = "${projectId}"
  account_id   = "${connectorServiceAccountId}"
  display_name = "P0 service account connector"
  description  = "Runs P0's service account connector."
}

resource "google_project_iam_member" "p0_service_account_connector" {
  project = "${projectId}"
  role    = google_project_iam_custom_role.p0_agentic_identity_manager.name
  # Terraform fills in the email at apply time.
  member = "serviceAccount:\${google_service_account.p0_service_account_connector.email}"
}

resource "google_cloud_run_v2_service" "p0_service_account_connector" {
  project             = "${projectId}"
  name                = "${connectorServiceName}"
  location            = "${region}"
  deletion_protection = false
  # P0 invokes the connector over HTTPS from outside GCP; access is gated by
  # roles/run.invoker and the connector's own token check, not by ingress.
  ingress = "INGRESS_TRAFFIC_ALL"

  template {
    service_account = google_service_account.p0_service_account_connector.email

    containers {
      image = "${CONNECTOR_IMAGE}"

      # The connector checks this against the caller's OIDC token, so it is
      # not redundant with the roles/run.invoker grant.
      env {
        name  = "INVOKER_SA_EMAIL"
        value = "${invokerServiceAccount}"
      }
    }
  }
}

resource "google_cloud_run_v2_service_iam_member" "p0_service_account_connector_invoker" {
  project  = "${projectId}"
  location = "${region}"
  name     = google_cloud_run_v2_service.p0_service_account_connector.name
  role     = "roles/run.invoker"
  member   = "serviceAccount:${invokerServiceAccount}"
}

${cloudRunViewerTf({
  resource: "p0_service_account_connector_viewer",
  project: `"${projectId}"`,
  location: `"${region}"`,
  service: "google_cloud_run_v2_service.p0_service_account_connector.name",
  member: invokerServiceAccount,
})}
`;
};

const iamWriteInstructions: GcpServiceAccountInstaller["components"]["iam-write"]["instructions"] =
  (context, projectId, item): Instructions<ReactNode> => {
    const gcloud = context.installed.gcloud as GcloudIntegration | undefined;
    const invokerServiceAccount =
      gcloud?.root?.[SINGLETON_ITEM]?.serviceAccountEmail ?? "";
    const connectorServiceAccountId =
      item.connectorServiceAccount?.split("@")[0] ?? "";

    return {
      commands: {
        iac: [
          {
            header: (
              <>
                <Paragraph>
                  P0 mints a short-lived service account in this project for
                  each agentic session, using a connector limited to one custom
                  role here.
                </Paragraph>
                <Paragraph>
                  P0&apos;s service account{" "}
                  <Text code>{invokerServiceAccount}</Text> needs{" "}
                  <Text code>roles/run.viewer</Text> to look up the
                  connector&apos;s URL.
                </Paragraph>
                <Paragraph>
                  Add the following to your Terraform, apply it, then click
                  &quot;Next&quot;:
                </Paragraph>
              </>
            ),
            command: gcpServiceAccountTerraform({
              projectId,
              region: item.connectorRegion,
              connectorServiceName: item.connectorServiceName,
              connectorServiceAccountId,
              invokerServiceAccount,
            }),
          },
        ],
      },
    };
  };

export const gcpServiceAccountInstaller: GcpServiceAccountInstaller = {
  components: {
    "iam-write": {
      // The identity project must already be a gcloud project, and the
      // installer additionally refuses one without Workload Identity
      // Federation.
      optionProvider: integrationOptionProvider("iam-write", [
        { component: "iam-write", integration: "gcloud" },
      ]),
      items: {},
      instructions: iamWriteInstructions,
    },
  },
};
